import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiClient } from '@/lib/apiClient';
import { connectInNewTab } from '@/lib/oauthConnect';
import type { MetaAccount, MetaConnectResponse, ApiResponse, EcommerceConnection } from '@/types/api';

export const useMetaIntegration = () => {
  const queryClient = useQueryClient();

  // Get Meta accounts
  const metaAccountsQuery = useQuery({
    queryKey: ['meta', 'accounts'],
    queryFn: () => 
      apiClient.get<ApiResponse<MetaAccount[]>>('/api/meta/accounts')
        .then(res => res.data.data || []),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Connect Meta account — opens Facebook OAuth in a new tab.
  const connectMetaMutation = useMutation({
    mutationFn: (redirectUrl: string = '/integrations') =>
      connectInNewTab(() =>
        apiClient.get<ApiResponse<MetaConnectResponse>>(`/api/meta/connect?redirectUrl=${redirectUrl}`)
          .then(res => res.data.data)
      ),
    onError: (error: any) => {
      toast.error('Failed to connect Meta account');
      console.error('Meta connection failed:', error);
    }
  });

  // Disconnect Meta account
  const disconnectMetaMutation = useMutation({
    mutationFn: (accountId: string) => 
      apiClient.delete(`/api/meta/accounts/${accountId}`),
    onSuccess: () => {
      toast.success('Meta account disconnected');
      queryClient.invalidateQueries({ queryKey: ['meta', 'accounts'] });
    },
    onError: () => {
      toast.error('Failed to disconnect Meta account');
    }
  });

  return {
    // Data
    metaAccounts: metaAccountsQuery.data || [],
    isLoading: metaAccountsQuery.isLoading,
    
    // Actions
    connectMeta: connectMetaMutation.mutate,
    disconnectMeta: disconnectMetaMutation.mutate,
    
    // States
    isConnecting: connectMetaMutation.isPending,
    isDisconnecting: disconnectMetaMutation.isPending,
  };
};

export const useEcommerceIntegration = () => {
  const queryClient = useQueryClient();

  // Get e-commerce connections. The API returns `storeType`/`lastSyncAt`/`shopDomain`,
  // but the UI's EcommerceConnection uses `platform`/`lastSync`/`storeUrl` — map them.
  const ecommerceQuery = useQuery({
    queryKey: ['ecommerce', 'connections'],
    queryFn: () =>
      apiClient.get<ApiResponse<any[]>>('/api/ecommerce/connections')
        .then(res => (res.data.data || []).map((c: any): EcommerceConnection => ({
          id: c.id,
          platform: c.storeType,
          storeName: c.storeName,
          storeUrl: c.storeUrl || c.shopDomain || '',
          isActive: c.isActive,
          lastSync: c.lastSyncAt ?? null,
          syncStatus: c.syncStatus || 'idle',
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
        }))),
    staleTime: 5 * 60 * 1000,
  });

  // Connect store
  const connectStoreMutation = useMutation({
    mutationFn: async (data: {
      platform: 'woocommerce' | 'shopify' | 'amazon' | 'salla' | 'ebay' | 'lazada';
      siteUrl?: string;
      consumerKey?: string;
      consumerSecret?: string;
      shop?: string;
      sellerId?: string;
      clientId?: string;
      clientSecret?: string;
      refreshToken?: string;
      marketplaceId?: string;
      accessToken?: string;
      appKey?: string;
    }) => {
      if (data.platform === 'woocommerce') {
        // WooCommerce connect
        return apiClient.post<ApiResponse<EcommerceConnection>>('/api/ecommerce/woocommerce/connect', {
          storeUrl: data.siteUrl,
          consumerKey: data.consumerKey,
          consumerSecret: data.consumerSecret,
        });
      } else if (data.platform === 'amazon') {
        // Amazon SP-API connect
        return apiClient.post<ApiResponse<EcommerceConnection>>('/api/ecommerce/amazon/connect', {
          sellerId: data.sellerId,
          clientId: data.clientId,
          clientSecret: data.clientSecret,
          refreshToken: data.refreshToken,
          marketplaceId: data.marketplaceId || 'ATVPDKIKX0DER',
        });
      } else if (data.platform === 'salla') {
        // Salla connect
        return apiClient.post<ApiResponse<EcommerceConnection>>('/api/ecommerce/salla/connect', {
          accessToken: data.accessToken,
        });
      } else if (data.platform === 'ebay') {
        // eBay connect
        return apiClient.post<ApiResponse<EcommerceConnection>>('/api/ecommerce/ebay/connect', {
          accessToken: data.accessToken,
        });
      } else if (data.platform === 'lazada') {
        // Lazada connect
        return apiClient.post<ApiResponse<EcommerceConnection>>('/api/ecommerce/lazada/connect', {
          accessToken: data.accessToken,
          appKey: data.appKey,
        });
      } else {
        // Shopify connect (OAuth redirect flow) — opens in a new tab.
        const shop = data.shop || '';
        return connectInNewTab(() =>
          apiClient.get<ApiResponse<{ authUrl: string }>>(`/api/ecommerce/shopify/connect?shop=${encodeURIComponent(shop)}`)
            .then(res => res.data.data)
        );
      }
    },
    onSuccess: () => {
      toast.success('Store connected successfully');
      queryClient.invalidateQueries({ queryKey: ['ecommerce', 'connections'] });
    },
    onError: () => {
      toast.error('Failed to connect store');
    }
  });

  // Sync store data
  const syncStoreMutation = useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.post(`/api/ecommerce/connections/${connectionId}/sync`),
    onSuccess: () => {
      toast.success('Store sync initiated');
      queryClient.invalidateQueries({ queryKey: ['ecommerce', 'connections'] });
    },
    onError: () => {
      toast.error('Failed to sync store');
    }
  });

  // Disconnect store
  const disconnectStoreMutation = useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.delete(`/api/ecommerce/connections/${connectionId}`),
    onSuccess: () => {
      toast.success('Store disconnected');
      queryClient.invalidateQueries({ queryKey: ['ecommerce', 'connections'] });
    },
    onError: () => {
      toast.error('Failed to disconnect store');
    }
  });

  return {
    // Data
    connections: ecommerceQuery.data || [],
    isLoading: ecommerceQuery.isLoading,

    // Actions
    connectStore: connectStoreMutation.mutate,
    // Promise-based variant for callers that need to know the outcome before
    // dismissing an inline form (e.g. WooCommerce's credentials form) —
    // Shopify's redirect flow doesn't need this since success navigates away.
    connectStoreAsync: connectStoreMutation.mutateAsync,
    syncStore: syncStoreMutation.mutate,
    disconnectStore: disconnectStoreMutation.mutate,

    // States
    isConnecting: connectStoreMutation.isPending,
    isSyncing: syncStoreMutation.isPending,
    isDisconnecting: disconnectStoreMutation.isPending,
  };
};
